import './style.css';
import { initAnalytics, track } from './analytics';
import { AudioRack } from './audio';
import {
  cancel,
  createDailyCourt,
  gather,
  getShotPreview,
  getSnapshot,
  pass,
  release,
  startRun,
  stepSimulation,
  TICK_MS,
  type GameSnapshot,
  type PlayerId,
  type PossessionResult,
} from './game';
import { renderOg } from './og';
import { CourtRenderer, type CourtFrame, type RenderPlayer } from './render';
import { shareResult, shareText } from './share';
import {
  daysBetween,
  loadProfile,
  markHelpSeen,
  recordCompletion,
  setMuted,
  utcDateKey,
  type Profile,
  type StoredResult,
} from './storage';

const query = new URLSearchParams(window.location.search);
if (query.has('og')) {
  renderOg();
} else {
  init();
}

function init(): void {
  const dateOverride = query.get('date');
  const dateKey = dateOverride && /^\d{4}-\d{2}-\d{2}$/.test(dateOverride) ? dateOverride : utcDateKey();
  const sliceMode = query.has('slice');
  const possessionCount = sliceMode ? 3 : 8;
  const forcedGeneratorVersion = query.has('v2') ? 2 : query.has('v1') ? 1 : undefined;
  const court = createDailyCourt(dateKey, {
    count: possessionCount,
    generatorVersion: forcedGeneratorVersion,
  });
  let run = startRun(court);
  let snapshot = getSnapshot(run);
  let profile = loadProfile(dateKey);
  const audio = new AudioRack();
  audio.setMuted(profile.muted);

  const canvas = byId<HTMLCanvasElement>('court');
  const renderer = new CourtRenderer(canvas);
  const helpDialog = byId<HTMLDialogElement>('help-dialog');
  const revealDialog = byId<HTMLDialogElement>('reveal-dialog');
  const startButton = byId<HTMLButtonElement>('start-button');
  const helpButton = byId<HTMLButtonElement>('help-button');
  const soundButton = byId<HTMLButtonElement>('sound-button');
  const retryButton = byId<HTMLButtonElement>('retry-button');
  const shareButton = byId<HTMLButtonElement>('share-button');
  const instruction = byId<HTMLElement>('instruction');
  const scoreValue = byId<HTMLElement>('score-value');
  const clockValue = byId<HTMLElement>('clock-value');
  const clockCell = document.querySelector<HTMLElement>('.score-cell--clock')!;
  const possessionValue = byId<HTMLElement>('possession-value');
  const possessionTotal = byId<HTMLElement>('possession-total');
  const pips = byId<HTMLElement>('possession-pips');
  const possessionCard = byId<HTMLElement>('possession-card');
  const actionCallout = byId<HTMLElement>('action-callout');
  const revealScore = byId<HTMLElement>('reveal-score');
  const revealTier = byId<HTMLElement>('reveal-tier');
  const revealContext = byId<HTMLElement>('reveal-context');
  const revealJourney = byId<HTMLElement>('reveal-journey');
  const nearMissCopy = byId<HTMLElement>('near-miss-copy');
  const shareStatus = byId<HTMLElement>('share-status');

  possessionTotal.textContent = `/${possessionCount}`;
  soundButton.textContent = profile.muted ? '×' : '♪';
  soundButton.setAttribute('aria-label', profile.muted ? 'Turn sound on' : 'Mute sound');

  initAnalytics(import.meta.env.VITE_UMAMI_WEBSITE_ID ?? '');
  trackVisit(profile, dateKey, court.puzzleNumber);
  track('session_start', { puzzle_number: court.puzzleNumber, generator_version: court.generatorVersion, attempt_number: attemptNumber(profile, dateKey), mode: sliceMode ? 'slice' : 'daily' });

  let lastFrame = performance.now();
  let accumulator = 0;
  let knownResults = 0;
  let activePointer: { id: number; x: number; y: number; gathering: boolean } | undefined;
  let resultAnimation: { result: PossessionResult; started: number; fromX: number; fromY: number } | undefined;
  let lastPossession = -1;
  let revealShown = false;
  let firstTouchGiven = false;

  function awaitingFirstTouch(state: GameSnapshot): boolean {
    return state.phase === 'live' && state.possessionIndex === 0 && !firstTouchGiven;
  }

  function loop(now: number): void {
    const delta = Math.min(80, now - lastFrame);
    lastFrame = now;
    const paused = helpDialog.open || revealDialog.open || Boolean(resultAnimation) || awaitingFirstTouch(snapshot);
    if (!paused) {
      accumulator += delta;
      while (accumulator >= TICK_MS) {
        const beforeCount = snapshot.results.length;
        snapshot = stepSimulation(run);
        accumulator -= TICK_MS;
        if (snapshot.results.length > beforeCount) beginResult(snapshot.lastResult!, now);
      }
    }

    if (resultAnimation && now - resultAnimation.started >= 760) {
      resultAnimation = undefined;
      accumulator = 0;
    }

    snapshot = getSnapshot(run);
    if (snapshot.possessionIndex !== lastPossession) {
      lastPossession = snapshot.possessionIndex;
      if (snapshot.phase === 'check') showPossessionCard(snapshot.possessionIndex + 1);
    }
    if (snapshot.phase === 'complete' && !revealShown && !resultAnimation) showReveal();

    updateHud(snapshot);
    renderer.render(toFrame(snapshot, now, resultAnimation));
    requestAnimationFrame(loop);
  }

  function beginResult(result: PossessionResult, now = performance.now()): void {
    if (knownResults >= snapshot.results.length && result.result !== 'expired') knownResults += 1;
    else knownResults = Math.max(knownResults, snapshot.results.length);
    const shooter = snapshot.offense.find((player) => player.id === result.shooter) ?? snapshot.offense[0]!;
    resultAnimation = { result, started: now, fromX: worldX(shooter.position.x), fromY: shooter.position.y };
    const callout = result.made
      ? `${result.value} — CLEAN`
      : result.result === 'expired'
        ? 'TOO LATE'
        : result.result === 'rushed'
          ? 'RUSHED'
          : result.alteredBy === 'help' ? 'HELP JUMPED IT' : 'HAND';
    showCallout(callout, !result.made);
    if (result.made) audio.play('swish');
    else if (result.result === 'expired') audio.play('horn');
    else if (result.result === 'altered') audio.play('block');
    else audio.play('rim');
    track('possession_complete', {
      possession_index: result.possessionIndex + 1,
      points: result.points,
      passes: result.passes,
      clock_bucket: clockBucket(result.clockTicksAtRelease / 60),
    });
  }

  function updateHud(state: GameSnapshot): void {
    scoreValue.textContent = String(state.score.points);
    clockValue.textContent = state.clockSeconds.toFixed(1);
    clockCell.classList.toggle('is-danger', state.phase === 'live' && state.clockSeconds <= 2);
    possessionValue.textContent = String(Math.min(state.possessionIndex + 1, state.possessionCount));
    instruction.textContent = state.phase === 'check'
      ? state.possessionIndex === 0
        ? 'Read the floor. Your first touch starts the clock.'
        : 'Read the floor. The clock starts on the slap.'
      : awaitingFirstTouch(state)
        ? 'Ball in. Your first touch starts the clock.'
        : state.phase === 'resolved'
        ? resultLine(state.lastResult)
        : state.phase === 'complete'
          ? `${state.possessionCount} in the book.`
          : state.ball.owner === null
            ? 'Defense shifting.'
            : state.shotPreview
              ? state.shotPreview.set ? 'The feet are set. Beat the hand.' : 'Gathering…'
              : 'Tap a teammate. Hold the ball to gather.';
    renderPips(state);
  }

  function renderPips(state: GameSnapshot): void {
    pips.innerHTML = '';
    for (let index = 0; index < state.possessionCount; index += 1) {
      const result = state.results[index];
      const pip = document.createElement('span');
      pip.className = `pip${result ? result.made ? ' is-make' : ' is-miss' : index === state.possessionIndex ? ' is-current' : ''}`;
      pip.textContent = result?.value ? String(result.value) : '';
      pips.append(pip);
    }
  }

  function toFrame(state: GameSnapshot, now: number, animation?: typeof resultAnimation): CourtFrame {
    const canPass = state.phase === 'live' && state.ball.owner !== null && !state.shotPreview;
    const shotProgress = animation ? Math.min(1, (now - animation.started) / 680) : 0;
    const offense: RenderPlayer[] = state.offense.map((player) => ({
      id: player.id,
      team: 'offense',
      x: worldX(player.position.x),
      y: player.position.y,
      vx: player.velocity.x * 2,
      vy: player.velocity.y,
      facingX: player.callingForBall
        ? state.ball.position.x - player.position.x
        : player.velocity.x || (player.position.x < .5 ? 1 : -1),
      hasBall: state.ball.owner === player.id,
      targetable: canPass && state.ballhandler !== player.id,
      action: animation?.result.shooter === player.id ? 'shoot' : player.gathering ? 'gather' : Math.hypot(player.velocity.x, player.velocity.y) > .0002 ? 'run' : 'idle',
      actionProgress: animation?.result.shooter === player.id ? Math.min(1, shotProgress * 1.7) : player.gatherProgress,
      ready: state.shotPreview?.set === true && state.ballhandler === player.id && !state.shotPreview.obstructed,
      threatened: state.shotPreview?.obstructed === true && state.ballhandler === player.id,
      callingForBall: player.callingForBall && !state.shotPreview,
      dimmed: Boolean(state.shotPreview && state.ballhandler !== player.id),
    }));
    const possessionPasses = state.passHistory.filter((entry) => entry.possessionIndex === state.possessionIndex);
    const currentPass = possessionPasses.at(-1);
    const previousPass = possessionPasses.at(-2);
    const featuredHelperId = state.ball.passingTo && previousPass?.helperId
      ? previousPass.helperId
      : currentPass?.helperId;
    const activeHelp = state.defense.some((defender) => defender.helpState !== 'home');
    const defense: RenderPlayer[] = state.defense.map((defender) => ({
      id: defender.id,
      team: 'defense',
      x: worldX(defender.position.x),
      y: defender.position.y,
      vx: defender.velocity.x * 2,
      vy: defender.velocity.y,
      facingX: defender.velocity.x,
      helpState: defender.helpState,
      featuredHelper: !state.shotPreview && defender.id === featuredHelperId && defender.helpState !== 'home',
      dimmed: state.shotPreview
        ? defender.id !== state.shotPreview.nearestDefenderId
        : activeHelp && defender.id !== featuredHelperId,
      action: animation?.result.defenderId === defender.id || state.shotPreview?.nearestDefenderId === defender.id
        ? 'contest'
        : defender.helpState === 'stunting'
          ? 'stunt'
          : defender.helpState === 'recovering'
            ? 'recover'
            : Math.hypot(defender.velocity.x, defender.velocity.y) > .0002
              ? 'run'
              : 'idle',
      actionProgress: animation?.result.defenderId === defender.id
        ? Math.min(1, shotProgress * 1.45)
        : state.shotPreview?.nearestDefenderId === defender.id
          ? Math.max(.25, Math.min(1, (0.3 - state.shotPreview.defenderDistance) / .19))
          : 0,
    }));
    const gatheringPlayer = state.shotPreview ? state.offense.find((player) => player.id === state.ballhandler) : undefined;
    const passReceiver = state.ball.passingTo
      ? state.offense.find((player) => player.id === state.ball.passingTo)
      : undefined;
    return {
      players: [...offense, ...defense],
      ball: {
        x: worldX(gatheringPlayer?.position.x ?? state.ball.position.x),
        y: gatheringPlayer?.position.y ?? state.ball.position.y,
        z: state.ball.passingTo
          ? Math.sin(state.ball.passProgress * Math.PI) * .2
          : state.shotPreview
            ? .12 + Math.min(1, state.shotPreview.gatherTicks / 14) * .17
            : 0,
        visible: !animation || animation.result.value === null,
        targetX: passReceiver ? worldX(passReceiver.position.x) : undefined,
        targetY: passReceiver?.position.y,
      },
      shot: animation && animation.result.value !== null ? {
        fromX: animation.fromX,
        fromY: animation.fromY,
        progress: shotProgress,
        made: animation.result.made,
        blocked: animation.result.result === 'altered',
      } : undefined,
      live: state.phase === 'live',
      danger: state.phase === 'live' && state.clockSeconds <= 2,
      flash: animation && shotProgress > .62 ? animation.result.made ? 'make' : animation.result.result === 'expired' ? 'horn' : animation.result.result === 'altered' ? 'block' : 'miss' : undefined,
      time: now / 1000,
    };
  }

  function showPossessionCard(number: number): void {
    possessionCard.textContent = number === possessionCount ? 'LAST BALL' : `BALL ${number}`;
    possessionCard.classList.remove('is-showing');
    void possessionCard.offsetWidth;
    possessionCard.classList.add('is-showing');
  }

  function showCallout(text: string, bad: boolean): void {
    actionCallout.textContent = text;
    actionCallout.className = `action-callout${bad ? ' is-bad' : ''}`;
    void actionCallout.offsetWidth;
    actionCallout.classList.add('is-showing');
  }

  function showReveal(): void {
    revealShown = true;
    const score = snapshot.score;
    const tier = tierFor(score.points, possessionCount);
    const pointsLeft = possessionCount * 3 - score.points;
    const beforeGames = profile.gamesPlayed;
    const update = recordCompletion(profile, dateKey, {
      score: score.points,
      timeLeftTenths: Math.round(score.timeLeftSeconds * 10),
      tier,
      journey: snapshot.results.map((result) => ({ made: result.made, value: result.value ?? 0, passes: result.passes })),
    });
    profile = update.profile;
    const best = profile.results[dateKey]!;

    if (beforeGames === 0) track('activation');
    track('session_complete', {
      score: score.points,
      score_tier: tier,
      time_left_tenths: Math.round(score.timeLeftSeconds * 10),
      streak_day: profile.currentStreak,
      attempt_number: best.attempts,
      personal_best: update.isBest,
      generator_version: court.generatorVersion,
      mode: sliceMode ? 'slice' : 'daily',
    });
    if (update.streakRepaired) track('streak_repaired');
    if (update.streakBroken) track('streak_broken', { prior_count: update.streakBroken.previous, best_count: update.streakBroken.best });
    if (tier === 'Automatic') track('top_tier');

    revealScore.textContent = `${score.points} PTS`;
    revealTier.textContent = tier;
    revealContext.textContent = update.isBest
      ? update.previousBest ? `New best · up from ${update.previousBest.score}` : `${(score.timeLeftSeconds).toFixed(1)} seconds banked`
      : `Today’s best: ${best.score} · ${(best.timeLeftTenths / 10).toFixed(1)}s banked`;
    nearMissCopy.hidden = pointsLeft === 0;
    revealJourney.innerHTML = '';
    for (const result of snapshot.results) {
      const shot = document.createElement('span');
      shot.className = `journey-shot ${result.made ? 'is-make' : 'is-miss'}`;
      const mark = document.createElement('b');
      mark.textContent = result.value ? `${result.made ? '●' : '○'}${result.value}` : '⏱';
      const passes = document.createElement('small');
      const routeNodes = Array.from({ length: Math.min(4, result.passes + 1) }, () => '●').join('›');
      passes.textContent = routeNodes + (result.passes > 3 ? '+' : '');
      passes.className = 'pass-route';
      if (result.passes >= 2) shot.classList.add('is-swing');
      shot.title = `${result.passes} pass${result.passes === 1 ? '' : 'es'}`;
      shot.append(mark, passes);
      revealJourney.append(shot);
    }
    nearMissCopy.textContent = pointsLeft === 0 ? '' : nearMiss(snapshot.results, pointsLeft);
    revealDialog.showModal();
  }

  function resetRun(): void {
    const best = profile.results[dateKey];
    track('retry', { previous_score: snapshot.score.points, attempt_number: (best?.attempts ?? 0) + 1 });
    run = startRun(court);
    snapshot = getSnapshot(run);
    knownResults = 0;
    resultAnimation = undefined;
    revealShown = false;
    firstTouchGiven = false;
    lastPossession = -1;
    accumulator = 0;
    revealDialog.close();
    track('session_start', { puzzle_number: court.puzzleNumber, generator_version: court.generatorVersion, attempt_number: attemptNumber(profile, dateKey), mode: sliceMode ? 'slice' : 'daily' });
  }

  canvas.addEventListener('pointerdown', (event) => {
    if (snapshot.phase !== 'live' || resultAnimation) return;
    const framePlayers = toFrame(snapshot, performance.now()).players;
    const id = renderer.hitTest(event.clientX, event.clientY, framePlayers);
    if (!id?.startsWith('o')) return;
    const playerId = id as PlayerId;
    if (playerId === snapshot.ballhandler && snapshot.ball.owner === playerId) {
      if (!gather(run)) return;
      firstTouchGiven = true;
      activePointer = { id: event.pointerId, x: event.clientX, y: event.clientY, gathering: true };
      canvas.setPointerCapture(event.pointerId);
      audio.play('plant');
      const player = snapshot.offense.find((candidate) => candidate.id === playerId)!;
      track('shot_started', {
        possession_index: snapshot.possessionIndex + 1,
        shot_value: shotValueFromPosition(player.position.x, player.position.y),
        openness_tier: openness(snapshot, playerId),
      });
    } else if (pass(run, playerId)) {
      firstTouchGiven = true;
      audio.play('pass');
      const from = snapshot.offense.find((candidate) => candidate.id === snapshot.ballhandler)!;
      const to = snapshot.offense.find((candidate) => candidate.id === playerId)!;
      track('pass', {
        possession_index: snapshot.possessionIndex + 1,
        from_zone: zone(from.position.x, from.position.y),
        to_zone: zone(to.position.x, to.position.y),
        clock_bucket: clockBucket(snapshot.clockSeconds),
      });
      markFirstPass();
    }
  });

  canvas.addEventListener('pointermove', (event) => {
    if (!activePointer?.gathering || activePointer.id !== event.pointerId) return;
    if (Math.hypot(event.clientX - activePointer.x, event.clientY - activePointer.y) > 78) {
      cancel(run);
      activePointer.gathering = false;
      showCallout('CANCELLED', false);
    }
  });

  const releasePointer = (event: PointerEvent) => {
    if (!activePointer || activePointer.id !== event.pointerId) return;
    if (activePointer.gathering) {
      const preview = getShotPreview(run);
      const result = release(run);
      snapshot = getSnapshot(run);
      if (result) {
        track('shot_released', {
          possession_index: result.possessionIndex + 1,
          shot_value: result.value ?? 0,
          result: result.result,
          gather_tier: result.gatherTicks < 14 ? 'rushed' : result.gatherTicks < 30 ? 'set' : 'held',
          contest_tier: preview ? preview.obstructed ? 'hand' : preview.defenderDistance < .18 ? 'closing' : 'clear' : 'unknown',
          clock_bucket: clockBucket(result.clockTicksAtRelease / 60),
        });
        beginResult(result);
      }
    }
    activePointer = undefined;
    if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
  };
  canvas.addEventListener('pointerup', releasePointer);
  canvas.addEventListener('pointercancel', (event) => {
    cancel(run);
    activePointer = undefined;
    if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
  });

  window.addEventListener('keydown', (event) => {
    if (event.code === 'Space' && !event.repeat && snapshot.phase === 'live') {
      event.preventDefault();
      if (gather(run)) firstTouchGiven = true;
    }
    if (/^Digit[123]$/.test(event.code) && snapshot.phase === 'live') {
      const id = `o${Number(event.code.at(-1)) - 1}` as PlayerId;
      if (pass(run, id)) firstTouchGiven = true;
    }
    if (event.code === 'Escape') cancel(run);
  });
  window.addEventListener('keyup', (event) => {
    if (event.code !== 'Space') return;
    const result = release(run);
    snapshot = getSnapshot(run);
    if (result) beginResult(result);
  });

  helpButton.addEventListener('click', () => {
    track('help_opened', { source: 'button', possession_index: snapshot.possessionIndex + 1 });
    if (!helpDialog.open) helpDialog.showModal();
  });
  startButton.addEventListener('click', () => {
    profile = markHelpSeen(profile);
    helpDialog.close();
    showPossessionCard(snapshot.possessionIndex + 1);
  });
  document.querySelectorAll<HTMLElement>('[data-close-dialog]').forEach((button) => button.addEventListener('click', () => helpDialog.close()));
  soundButton.addEventListener('click', () => {
    profile = setMuted(profile, !profile.muted);
    audio.setMuted(profile.muted);
    soundButton.textContent = profile.muted ? '×' : '♪';
    soundButton.setAttribute('aria-label', profile.muted ? 'Turn sound on' : 'Mute sound');
  });
  retryButton.addEventListener('click', resetRun);
  shareButton.addEventListener('click', async () => {
    const best = profile.results[dateKey];
    if (!best) return;
    const method = await shareResult(shareText(court.puzzleNumber, best, profile.currentStreak));
    shareStatus.textContent = method === 'dismissed' ? '' : 'COPIED';
  });
  window.addEventListener('resize', () => renderer.resize());

  if (!profile.helpSeen) {
    track('help_opened', { source: 'first_visit', possession_index: 1 });
    helpDialog.showModal();
  } else {
    showPossessionCard(1);
  }
  requestAnimationFrame(loop);
}

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing #${id}`);
  return element as T;
}

function worldX(x: number): number { return (x - .5) * 2; }
function shotValueFromPosition(x: number, y: number): 2 | 3 { return Math.hypot(x - .5, y - .08) >= .52 || (y < .53 && Math.abs(x - .5) > .29) ? 3 : 2; }
function clockBucket(seconds: number): string { return seconds <= 2 ? '0-2' : seconds <= 5 ? '2-5' : '5-8'; }
function zone(x: number, y: number): string { if (y < .32 && Math.abs(x - .5) > .28) return 'corner'; if (y < .42) return 'paint'; if (Math.abs(x - .5) < .16) return 'slot'; return 'wing'; }
function openness(state: GameSnapshot, id: PlayerId): string {
  const player = state.offense.find((candidate) => candidate.id === id)!;
  const distance = Math.min(...state.defense.map((defender) => Math.hypot(defender.position.x - player.position.x, defender.position.y - player.position.y)));
  return distance > .25 ? 'clear' : distance > .16 ? 'closing' : 'smothered';
}
function nearMiss(results: readonly PossessionResult[], pointsLeft: number): string {
  if (results.some((result) => result.result === 'expired')) return 'Horn got one.';
  const altered = results.filter((result) => result.result === 'altered');
  if (altered.length > 0 && altered.every((result) => result.alteredBy === 'help')) return 'Help jumped the catch. Swing it again.';
  if (altered.length > 0) return 'Hand got there.';
  if (results.some((result) => result.result === 'rushed')) return 'Left before the feet.';
  if (pointsLeft === 1) return 'One point from perfect.';
  if (pointsLeft > 1) return `${pointsLeft} points from perfect.`;
  return 'Clean. Now beat the clock.';
}
function resultLine(result: PossessionResult | null): string {
  if (!result) return 'Next ball.';
  if (result.made) return result.value === 3 ? 'Three. Clean.' : 'Two.';
  if (result.result === 'expired') return 'Horn.';
  if (result.result === 'altered') {
    return result.alteredBy === 'help' ? 'Help jumped the catch. Swing it again.' : 'Hand got it. Release sooner.';
  }
  return 'Left early.';
}
function tierFor(points: number, count: number): StoredResult['tier'] {
  const ratio = points / (count * 3);
  if (ratio >= 23 / 24) return 'Automatic';
  if (ratio >= 19 / 24) return 'Closer';
  if (ratio >= 14 / 24) return 'Bucket Getter';
  if (ratio >= 8 / 24) return 'In Rhythm';
  return 'Finding Rhythm';
}
function attemptNumber(profile: Profile, dateKey: string): number { return (profile.results[dateKey]?.attempts ?? 0) + 1; }
function trackVisit(profile: Profile, dateKey: string, puzzle: number): void {
  const days = Math.max(0, daysBetween(profile.firstSeen, dateKey));
  try {
    if (localStorage.getItem('closeout_first_seen_tracked') !== '1') {
      localStorage.setItem('closeout_first_seen_tracked', '1');
      track('first_seen');
    }
  } catch { track('first_seen'); }
  track('visit', { days_since_first: days, puzzle_number: puzzle });
  if (days > 0) track('return_visit', { days_since_first: days });
}
function markFirstPass(): void {
  try {
    if (localStorage.getItem('closeout_first_pass_tracked') === '1') return;
    localStorage.setItem('closeout_first_pass_tracked', '1');
    track('first_pass');
  } catch { /* analytics is best-effort */ }
}
